import {
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  normalizeStatusKey,
  StatusPill,
} from '@/components/common/DataTable/StatusPill';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useIntakesSetStatusCreateMutation } from '@/services/api/intakeService';
import { IntakeStatus, IntakeStatusValue } from '@/services/types/intake-types';

import { RejectionReasonSelect } from './RejectionReasonSelect';

interface LeadNotificationResult {
  notified: boolean;
  actions: string[];
  error: string | null;
}

const ACTION_PAST_TENSE: Record<string, string> = {
  evaluate: 'evaluated',
  accept: 'accepted',
  decline: 'declined',
};

const STATUS_OPTIONS: readonly { value: IntakeStatusValue; label: string }[] = [
  { value: IntakeStatus.IN_PROGRESS, label: 'In Progress' },
  { value: IntakeStatus.CHASE, label: 'Chase' },
  { value: IntakeStatus.SENT_FOR_SIGNATURE, label: 'Sent for Signature' },
  { value: IntakeStatus.SIGNED, label: 'Signed' },
  { value: IntakeStatus.REJECTED, label: 'Rejected' },
  { value: IntakeStatus.LOST, label: 'Lost' },
  { value: IntakeStatus.ESCALATED, label: 'Escalated' },
  // Archived intentionally omitted; use the Archive action instead
] as const;

const ICON_COLOR_MAP: Record<IntakeStatusValue, string> = {
  [IntakeStatus.IN_PROGRESS]: '#6b21a8', // purple-800
  [IntakeStatus.CHASE]: '#92400e', // amber-800
  [IntakeStatus.SENT_FOR_SIGNATURE]: '#1e40af', // blue-800
  [IntakeStatus.SIGNED]: '#065f46', // emerald-800
  [IntakeStatus.REJECTED]: '#991b1b', // red-800
  [IntakeStatus.ARCHIVED]: '#1f2937', // gray-800
  [IntakeStatus.LOST]: '#000000', // black
  [IntakeStatus.ESCALATED]: '#ea580c', // orange-600
};

export const StatusSelect: React.FC<{
  intakeId: string;
  value: string;
  size?: 'small' | 'medium';
  className?: string;
  hasActiveChase?: boolean;
  hasLinkedLead?: boolean;
}> = ({ intakeId, value, size = 'small', className, hasActiveChase, hasLinkedLead }) => {
  const [setStatus, { isLoading }] = useIntakesSetStatusCreateMutation();
  const normalizedValue = normalizeStatusKey(value) as IntakeStatusValue;
  const iconColor = ICON_COLOR_MAP[normalizedValue] || 'inherit';

  // State for rejection reason dialog
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [otherRejectionReason, setOtherRejectionReason] = useState<string>('');

  // State for chase stop confirmation dialog
  const [showChaseStopDialog, setShowChaseStopDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<IntakeStatusValue | null>(null);

  // State for Lexamica confirmation dialog (shown before rejection when linked lead exists)
  const [showLexamicaConfirm, setShowLexamicaConfirm] = useState(false);

  const applyStatus = async (next: IntakeStatusValue) => {
    try {
      await setStatus({ id: intakeId, status: next }).unwrap();
      toast.success('Intake status updated');
    } catch (err) {
      toast.error('Failed to update status');
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const handleChange = async (e: SelectChangeEvent<string>) => {
    const next = e.target.value as IntakeStatusValue;

    // If selecting Rejected, show the Lexamica confirmation first if linked lead exists
    if (next === IntakeStatus.REJECTED) {
      setRejectionReason('');
      setOtherRejectionReason('');
      if (hasLinkedLead) {
        setShowLexamicaConfirm(true);
      } else {
        setShowRejectionDialog(true);
      }
      return;
    }

    // If moving away from Chase status and there's an active chase, confirm first
    if (
      hasActiveChase &&
      normalizedValue === IntakeStatus.CHASE &&
      next !== IntakeStatus.CHASE
    ) {
      setPendingStatus(next);
      setShowChaseStopDialog(true);
      return;
    }

    await applyStatus(next);
  };

  const handleConfirmRejection = async () => {
    if (!rejectionReason) {
      toast.error('Please select a rejection reason');
      return;
    }

    // Combine rejection reason with custom text if "Other" is selected
    const finalRejectionReason =
      rejectionReason === 'Other' && otherRejectionReason
        ? `Other: ${otherRejectionReason}`
        : rejectionReason;

    try {
      const result = await setStatus({
        id: intakeId,
        status: IntakeStatus.REJECTED,
        rejection_reason: finalRejectionReason,
      }).unwrap();
      toast.success('Intake status updated to Rejected');
      setShowRejectionDialog(false);
      setRejectionReason('');
      setOtherRejectionReason('');

      // Show Lexamica notification result toast
      const notification = (result as unknown as { lead_notification?: LeadNotificationResult })
        ?.lead_notification;
      if (notification) {
        if (notification.error) {
          toast.error(
            `Lexamica update failed: ${notification.error}. Lead status was rolled back.`,
            { duration: 8000 },
          );
        } else if (notification.notified && notification.actions.length > 0) {
          const actionList = notification.actions
            .map((a) => ACTION_PAST_TENSE[a] ?? a)
            .join(', ');
          toast.success(`Case ${actionList} in Lexamica`, { duration: 5000 });
        }
      }
    } catch (err) {
      toast.error('Failed to update status');
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const handleCancelRejection = () => {
    setShowRejectionDialog(false);
    setRejectionReason('');
    setOtherRejectionReason('');
  };

  return (
    <>
      <FormControl size={size} className={className} sx={{ minWidth: 10 }}>
        <Select
          value={normalizedValue}
          onChange={handleChange}
          displayEmpty
          disabled={isLoading}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
            backgroundColor: 'transparent',
            p: 0,
            height: 28,
            '& .MuiSelect-select': { p: 0 },
            '& .MuiSelect-icon': { right: 0, color: iconColor },
            minWidth: 160,
            // Keep visual brightness even when disabled during save
            '&.Mui-disabled': { opacity: 1 },
          }}
          renderValue={(selected) => <StatusPill status={selected as string} />}
          MenuProps={{
            PaperProps: { sx: { maxHeight: 320 } },
          }}
          aria-label="Update intake status"
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} dense>
              <StatusPill status={opt.value} />
            </MenuItem>
          ))}
        </Select>
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          >
            <CircularProgress size={16} />
          </div>
        )}
      </FormControl>

      {/* Rejection Reason Dialog */}
      <Dialog open={showRejectionDialog} onOpenChange={setShowRejectionDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Rejection Reason</DialogTitle>
            <DialogDescription>
              Please select a reason for rejecting this intake.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <RejectionReasonSelect
              value={rejectionReason}
              onChange={setRejectionReason}
              otherReason={otherRejectionReason}
              onOtherReasonChange={setOtherRejectionReason}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelRejection}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRejection}
              disabled={
                isLoading ||
                !rejectionReason ||
                (rejectionReason === 'Other' && !otherRejectionReason.trim())
              }
            >
              {isLoading ? 'Saving...' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lexamica Confirmation Dialog (before rejection) */}
      <Dialog open={showLexamicaConfirm} onOpenChange={setShowLexamicaConfirm}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Lexamica Case Transition</DialogTitle>
            <DialogDescription>
              This intake is linked to a Lexamica lead. Rejecting this intake
              will decline the case in Lexamica.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLexamicaConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowLexamicaConfirm(false);
                setShowRejectionDialog(true);
              }}
            >
              Continue with Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chase Stop Confirmation Dialog */}
      <Dialog open={showChaseStopDialog} onOpenChange={setShowChaseStopDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Stop Auto Chase?</DialogTitle>
            <DialogDescription>
              This intake has an active auto chase in progress. Changing the
              status will stop the auto chase and cancel all remaining scheduled
              messages.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowChaseStopDialog(false);
                setPendingStatus(null);
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                setShowChaseStopDialog(false);
                if (pendingStatus) {
                  await applyStatus(pendingStatus);
                  setPendingStatus(null);
                }
              }}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Change Status & Stop Chase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StatusSelect;
